import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Users, Handshake, CalendarCheck } from "lucide-react"
import { Badge } from "src/components/ui/badge"
import { Skeleton } from "src/components/ui/skeleton"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"

import { fetchLeads } from "src/features/leads/api/leadsApi"
import { fetchDeals } from "src/features/deals/api/dealsApi"
import { fetchBookings } from "src/features/bookings/api/bookingsApi"
import {
  formatMoney, DEAL_STATUS_BADGE, DEAL_STATUS_LABEL,
} from "src/features/deals/constants"
import { BOOKING_STATUS_BADGE, BOOKING_STATUS_LABEL } from "src/features/bookings/constants"

const TABS = [
  { id: "leads", label: "Leads", icon: Users },
  { id: "deals", label: "Deals", icon: Handshake },
  { id: "bookings", label: "Bookings", icon: CalendarCheck },
]

// The three sales tabs on the project dashboard, each scoped to this project.
export function ProjectActivityTabs({ projectId }) {
  const navigate = useNavigate()
  const [tab, setTab] = useState("leads")
  const [data, setData] = useState({ leads: null, deals: null, bookings: null })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!projectId || data[tab] !== null) return
    setLoading(true)
    const loaders = {
      leads:    () => fetchLeads({ project_id: projectId, per_page: 50 }).then((d) => d.leads || []),
      deals:    () => fetchDeals({ project_id: projectId, per_page: 50 }).then((d) => d.deals || []),
      bookings: () => fetchBookings({ project_id: projectId, per_page: 50 }).then((d) => d.bookings || []),
    }
    loaders[tab]()
      .then((rows) => setData((cur) => ({ ...cur, [tab]: rows })))
      .catch(() => setData((cur) => ({ ...cur, [tab]: [] })))
      .finally(() => setLoading(false))
  }, [tab, projectId]) // eslint-disable-line react-hooks/exhaustive-deps

  const rows = data[tab]

  return (
    <div>
      {/* Tabs */}
      <div className="mb-3 flex gap-5 border-b border-slate-200 text-sm">
        {TABS.map((t) => {
          const Icon = t.icon
          const count = data[t.id]?.length
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`-mb-px flex items-center gap-1.5 border-b-2 py-2 ${tab === t.id ? "border-slate-900 font-medium text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            >
              <Icon className="h-4 w-4" /> {t.label}
              {count != null && (
                <span className="rounded-full bg-slate-100 px-1.5 text-[11px] text-slate-500">{count}</span>
              )}
            </button>
          )
        })}
      </div>

      {loading && !rows ? (
        <Skeleton className="h-40 w-full rounded-xl" />
      ) : !rows || rows.length === 0 ? (
        <div className="flex h-32 items-center justify-center rounded-xl border border-dashed border-slate-200 text-sm text-slate-400">
          No {tab} for this project yet.
        </div>
      ) : tab === "leads" ? (
        <ActivityTable
          head={["Name", "Phone", "Status", "Source"]}
          rows={rows}
          onRowClick={(r) => navigate(`/leads/${r.id}`)}
          cells={(r) => [
            <span className="font-medium text-slate-800">{r.name}</span>,
            r.phone || "—",
            <span className="capitalize text-slate-600">{r.status}</span>,
            <span className="capitalize text-slate-600">{r.source || "—"}</span>,
          ]}
        />
      ) : tab === "deals" ? (
        <ActivityTable
          head={["Deal", "Stage", "Status", "Value"]}
          rows={rows}
          onRowClick={(r) => navigate(`/deals/${r.id}`)}
          cells={(r) => [
            <span className="font-medium text-slate-800">{r.name || `Deal #${r.id}`}</span>,
            r.stage?.name || "—",
            <Badge variant={DEAL_STATUS_BADGE[r.status] || "secondary"}>{DEAL_STATUS_LABEL[r.status] || r.status}</Badge>,
            formatMoney(r.value),
          ]}
        />
      ) : (
        <ActivityTable
          head={["Customer", "Unit", "Status", "Collected"]}
          rows={rows}
          onRowClick={(r) => navigate(`/bookings/${r.id}`)}
          cells={(r) => [
            <span className="font-medium text-slate-800">{r.customer?.name || "—"}</span>,
            r.unit?.unit_number || "—",
            <Badge variant={BOOKING_STATUS_BADGE[r.status] || "secondary"}>{BOOKING_STATUS_LABEL[r.status] || r.status}</Badge>,
            `${formatMoney(r.amount_received)} / ${formatMoney(r.total_value)}`,
          ]}
        />
      )}
    </div>
  )
}

function ActivityTable({ head, rows, cells, onRowClick }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <Table>
        <TableHeader>
          <TableRow><TableHead className="w-[50px]">#</TableHead>{head.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, idx) => (
            <TableRow key={r.id} className="cursor-pointer" onClick={() => onRowClick(r)}>
              <TableCell className="text-xs text-slate-400">{idx + 1}</TableCell>
              {cells(r).map((c, i) => <TableCell key={i}>{c}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
